import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BankLayout from './BankLayout';
import { createPayee, createTransfer, fetchDashboard, logoutUser } from './apiClient';

const initialPayeeForm = {
  name: '',
  nickname: '',
  bankName: '',
  accountNumber: '',
  confirmAccountNumber: '',
  ifsc: '',
  limit: '',
};

const initialTransferForm = {
  amount: '',
  reference: '',
};

function AccountPage() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    profile: null,
    accounts: [],
    payees: [],
    activity: [],
  });
  const [payeeForm, setPayeeForm] = useState(initialPayeeForm);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [transferForm, setTransferForm] = useState(initialTransferForm);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [transferFeedback, setTransferFeedback] = useState('');
  const [transferError, setTransferError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      try {
        const nextState = await fetchDashboard();
        if (!active) return;
        setState(nextState);
      } catch (_error) {
        if (!active) return;
        navigate('/');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDashboard();
    return () => {
      active = false;
    };
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (_error) {
    }
    navigate('/');
  };

  const handlePayeeChange = (field, value) => {
    setPayeeForm(current => ({
      ...current,
      [field]: value,
    }));
  };

  const handlePayeeSubmit = async event => {
    event.preventDefault();
    setFeedback('');
    setError('');

    if (Object.values(payeeForm).some(value => !String(value).trim())) {
      setError('Enter all payee details before adding the beneficiary.');
      return;
    }

    if (payeeForm.accountNumber !== payeeForm.confirmAccountNumber) {
      setError('Account number and confirmation do not match.');
      return;
    }

    try {
      const response = await createPayee(payeeForm);
      setState(current => ({
        ...current,
        payees: response.payees,
      }));
      setPayeeForm(initialPayeeForm);
      setFeedback('Payee added successfully to the beneficiary list.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to add payee.');
    }
  };

  const handleTransferSubmit = async event => {
    event.preventDefault();
    setTransferFeedback('');
    setTransferError('');

    if (!selectedPayee) {
      setTransferError('Select a payee before initiating a transfer.');
      return;
    }

    if (!transferForm.amount.trim() || !transferForm.reference.trim()) {
      setTransferError('Enter the transfer amount and reference.');
      return;
    }

    const transferAmount = Number(transferForm.amount);
    if (Number.isNaN(transferAmount) || transferAmount <= 0) {
      setTransferError('Enter a valid transfer amount.');
      return;
    }

    const payeeLimit = Number(String(selectedPayee.limit).replace(/[^0-9.]/g, '').replace(/,/g, ''));
    if (!Number.isNaN(payeeLimit) && transferAmount > payeeLimit) {
      setTransferError('Transfer amount exceeds the configured payee limit.');
      return;
    }

    try {
      const response = await createTransfer({
        payeeId: selectedPayee.id,
        amount: transferAmount,
        reference: transferForm.reference.trim(),
      });
      setState(current => ({
        ...current,
        accounts: response.accounts,
        activity: response.activity,
      }));
      setTransferForm(initialTransferForm);
      setTransferFeedback(`Transfer completed successfully to ${selectedPayee.name}.`);
    } catch (requestError) {
      setTransferError(requestError.response?.data?.error || 'Unable to complete transfer.');
    }
  };

  if (loading) {
    return (
      <BankLayout pageTitle="Account Summary" strapline="Loading your Load Bank dashboard.">
        <section style={styles.headerStrip}>
          <div style={styles.meta}>Loading account details...</div>
        </section>
      </BankLayout>
    );
  }

  if (!state.profile) {
    return null;
  }

  return (
    <BankLayout
      pageTitle="Account Summary"
      strapline="Review balances, recent activity, and maintain your registered payees from one dashboard."
    >
      <section style={styles.headerStrip}>
        <div>
          <div style={styles.welcome}>Welcome back, {state.profile.fullName}</div>
          <div style={styles.meta}>
            CIF {state.profile.customerId} | {state.profile.branch} | Last login: 18 Feb 2026, 08:42 IST
          </div>
        </div>

        <button type="button" style={styles.logoutButton} onClick={handleLogout}>
          Logout
        </button>
      </section>

      <section style={styles.cardGrid}>
        {state.accounts.map(account => (
          <article key={account.id} style={styles.accountCard}>
            <div style={styles.accountLabel}>{account.label}</div>
            <div style={styles.accountNumber}>{account.number}</div>
            <div style={styles.accountBalance}>{account.balance}</div>
            <div style={styles.accountMeta}>Available: {account.available}</div>
            <div style={styles.accountStatus}>{account.status}</div>
          </article>
        ))}
      </section>

      <section style={styles.contentGrid}>
        <div style={styles.panel}>
          <div style={styles.panelHeader}>Recent Transactions</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Narration</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {state.activity.map(item => (
                  <tr key={item.id}>
                    <td style={styles.td}>{item.date}</td>
                    <td style={styles.td}>{item.narration}</td>
                    <td
                      style={{
                        ...styles.td,
                        color: item.amount.startsWith('+') ? 'var(--sbi-success)' : '#a33d22',
                        fontWeight: 700,
                      }}
                    >
                      {item.amount}
                    </td>
                    <td style={styles.td}>{item.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={styles.panel}>
          <div style={styles.panelHeader}>Add Payee</div>
          <form style={styles.payeeForm} onSubmit={handlePayeeSubmit}>
            <label style={styles.label}>
              Payee Name
              <input
                style={styles.input}
                value={payeeForm.name}
                onChange={event => handlePayeeChange('name', event.target.value)}
                placeholder="Beneficiary name"
              />
            </label>

            <label style={styles.label}>
              Nickname
              <input
                style={styles.input}
                value={payeeForm.nickname}
                onChange={event => handlePayeeChange('nickname', event.target.value)}
                placeholder="Friendly label"
              />
            </label>

            <label style={styles.label}>
              Bank Name
              <input
                style={styles.input}
                value={payeeForm.bankName}
                onChange={event => handlePayeeChange('bankName', event.target.value)}
                placeholder="Bank / branch"
              />
            </label>

            <label style={styles.label}>
              Account Number
              <input
                style={styles.input}
                value={payeeForm.accountNumber}
                onChange={event => handlePayeeChange('accountNumber', event.target.value)}
                placeholder="Account number"
              />
            </label>

            <label style={styles.label}>
              Confirm Account Number
              <input
                style={styles.input}
                value={payeeForm.confirmAccountNumber}
                onChange={event => handlePayeeChange('confirmAccountNumber', event.target.value)}
                placeholder="Re-enter account number"
              />
            </label>

            <label style={styles.label}>
              IFSC Code
              <input
                style={styles.input}
                value={payeeForm.ifsc}
                onChange={event => handlePayeeChange('ifsc', event.target.value)}
                placeholder="SBIN0000000"
              />
            </label>

            <label style={styles.label}>
              Transfer Limit
              <input
                style={styles.input}
                value={payeeForm.limit}
                onChange={event => handlePayeeChange('limit', event.target.value)}
                placeholder="Rs 50,000"
              />
            </label>

            {error ? <div style={styles.error}>{error}</div> : null}
            {feedback ? <div style={styles.feedback}>{feedback}</div> : null}

            <button type="submit" style={styles.primaryButton}>
              Add Payee
            </button>
          </form>
        </div>
      </section>

      <section style={styles.transferGrid}>
        <section style={styles.panel}>
          <div style={styles.panelHeader}>Transfer To Payee</div>
          <form style={styles.payeeForm} onSubmit={handleTransferSubmit}>
            <label style={styles.label}>
              Select Payee
              <select
                style={styles.input}
                value={selectedPayee ? selectedPayee.id : ''}
                onChange={event => {
                  const payee = state.payees.find(item => item.id === event.target.value) || null;
                  setSelectedPayee(payee);
                  setTransferFeedback('');
                  setTransferError('');
                }}
              >
                <option value="">Choose registered payee</option>
                {state.payees.map(payee => (
                  <option key={payee.id} value={payee.id}>
                    {payee.name} ({payee.accountNumber})
                  </option>
                ))}
              </select>
            </label>

            {selectedPayee ? (
              <div style={styles.transferSummary}>
                Bank: {selectedPayee.bankName} | IFSC: {selectedPayee.ifsc} | Limit: {selectedPayee.limit}
              </div>
            ) : null}

            <label style={styles.label}>
              Amount
              <input
                style={styles.input}
                value={transferForm.amount}
                onChange={event =>
                  setTransferForm(current => ({ ...current, amount: event.target.value }))
                }
                placeholder="Rs 10,000"
              />
            </label>

            <label style={styles.label}>
              Reference
              <input
                style={styles.input}
                value={transferForm.reference}
                onChange={event =>
                  setTransferForm(current => ({ ...current, reference: event.target.value }))
                }
                placeholder="Reason for transfer"
              />
            </label>

            {transferError ? <div style={styles.error}>{transferError}</div> : null}
            {transferFeedback ? <div style={styles.feedback}>{transferFeedback}</div> : null}

            <button type="submit" style={styles.primaryButton}>
              Transfer Now
            </button>
          </form>
        </section>

        <section style={styles.panel}>
          <div style={styles.panelHeader}>Registered Payees</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Payee Name</th>
                  <th style={styles.th}>Nickname</th>
                  <th style={styles.th}>Bank</th>
                  <th style={styles.th}>Account</th>
                  <th style={styles.th}>IFSC</th>
                  <th style={styles.th}>Limit</th>
                  <th style={styles.th}>Added On</th>
                </tr>
              </thead>
              <tbody>
                {state.payees.map(payee => (
                  <tr
                    key={payee.id}
                    style={selectedPayee?.id === payee.id ? styles.selectedRow : undefined}
                  >
                    <td style={styles.td}>{payee.name}</td>
                    <td style={styles.td}>{payee.nickname}</td>
                    <td style={styles.td}>{payee.bankName}</td>
                    <td style={styles.td}>{payee.accountNumber}</td>
                    <td style={styles.td}>{payee.ifsc}</td>
                    <td style={styles.td}>{payee.limit}</td>
                    <td style={styles.td}>{payee.addedOn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </section>
    </BankLayout>
  );
}

const styles = {
  headerStrip: {
    alignItems: 'center',
    background: '#fff',
    border: '1px solid var(--sbi-border)',
    borderRadius: 14,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'space-between',
    marginBottom: 18,
    padding: '18px 20px',
  },
  welcome: {
    color: 'var(--sbi-deep-blue)',
    fontSize: 22,
    fontWeight: 700,
  },
  meta: {
    color: 'var(--sbi-muted)',
    fontSize: 14,
    marginTop: 5,
  },
  logoutButton: {
    background: '#fff',
    border: '1px solid #b4c6e5',
    borderRadius: 8,
    color: 'var(--sbi-deep-blue)',
    cursor: 'pointer',
    fontWeight: 700,
    padding: '10px 14px',
  },
  cardGrid: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    marginBottom: 18,
  },
  accountCard: {
    background: 'linear-gradient(180deg, #ffffff 0%, var(--sbi-panel) 100%)',
    border: '1px solid var(--sbi-border)',
    borderRadius: 14,
    padding: 18,
  },
  accountLabel: {
    color: 'var(--sbi-blue)',
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  accountNumber: {
    color: 'var(--sbi-muted)',
    fontSize: 14,
    marginTop: 8,
  },
  accountBalance: {
    color: 'var(--sbi-deep-blue)',
    fontSize: 30,
    fontWeight: 700,
    marginTop: 14,
  },
  accountMeta: {
    color: 'var(--sbi-text)',
    fontSize: 14,
    marginTop: 8,
  },
  accountStatus: {
    color: 'var(--sbi-success)',
    fontSize: 13,
    fontWeight: 700,
    marginTop: 12,
  },
  contentGrid: {
    display: 'grid',
    gap: 18,
    gridTemplateColumns: 'minmax(0, 1.45fr) minmax(300px, 1fr)',
    marginBottom: 18,
  },
  transferGrid: {
    display: 'grid',
    gap: 18,
    gridTemplateColumns: 'minmax(300px, 380px) minmax(0, 1fr)',
  },
  panel: {
    background: '#fff',
    border: '1px solid var(--sbi-border)',
    borderRadius: 14,
    overflow: 'hidden',
  },
  panelHeader: {
    background: '#edf3ff',
    borderBottom: '1px solid var(--sbi-border)',
    color: 'var(--sbi-deep-blue)',
    fontSize: 17,
    fontWeight: 700,
    padding: '14px 18px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
  },
  th: {
    background: '#f7faff',
    borderBottom: '1px solid #dbe5f5',
    color: 'var(--sbi-deep-blue)',
    fontSize: 13,
    padding: '12px 14px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  td: {
    borderBottom: '1px solid #e7eef9',
    color: 'var(--sbi-text)',
    fontSize: 14,
    padding: '12px 14px',
    verticalAlign: 'top',
  },
  payeeForm: {
    display: 'grid',
    gap: 14,
    padding: 18,
  },
  label: {
    color: 'var(--sbi-deep-blue)',
    display: 'block',
    fontSize: 14,
    fontWeight: 700,
  },
  input: {
    border: '1px solid #b7c7e3',
    borderRadius: 8,
    display: 'block',
    marginTop: 8,
    padding: '11px 12px',
    width: '100%',
  },
  error: {
    color: '#c43b23',
    fontSize: 13,
    fontWeight: 700,
  },
  feedback: {
    color: 'var(--sbi-success)',
    fontSize: 13,
    fontWeight: 700,
  },
  transferSummary: {
    background: '#f6f9ff',
    border: '1px solid #d7e3f7',
    borderRadius: 10,
    color: 'var(--sbi-muted)',
    fontSize: 13,
    lineHeight: 1.5,
    padding: '10px 12px',
  },
  selectedRow: {
    background: '#f9fbff',
  },
  primaryButton: {
    background: 'linear-gradient(180deg, #f2ba52 0%, var(--sbi-gold) 100%)',
    border: '1px solid #cb8f1b',
    borderRadius: 8,
    color: '#21314d',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 700,
    padding: '12px 22px',
  },
};

export default AccountPage;
